import config from '@/config';
import fetchWrapper from '@/services/fetchWrapper.service';

function hasValidAuctionStartingPrice(artworkAbout) {
  if (!artworkAbout || typeof artworkAbout !== 'object') {
    return false;
  }
  if (!Object.prototype.hasOwnProperty.call(artworkAbout, 'price_starting_at')) {
    return false;
  }
  const value = artworkAbout.price_starting_at;
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return false;
    }
    const numeric = Number(trimmed);
    return Number.isFinite(numeric);
  }
  return false;
}

function removeArtworkDraft(draftId) {
  return fetchWrapper.delete(`${config.apis.api.url}/draft/${draftId}`, {
    injectToken: true,
  });
}

function deleteArtwork(artworkId) {
  return fetchWrapper.delete(`${config.apis.api.url}/artwork/${artworkId}`, {
    injectToken: true,
  });
}

function getArtworkForEdit(slug) {
  return fetchWrapper.get(`${config.apis.api.url}/artwork/${slug}`, {
    injectToken: true,
  });
}

function saveArtworkDraft(formState, validationOptions) {
  const isFullValidation = Boolean(
    validationOptions?.about &&
    validationOptions?.media &&
    validationOptions?.shipping
  );

  const scheduleFields = {
    ...(formState.publishedAt !== undefined && { publishedAt: formState.publishedAt }),
    ...(formState.publicationDate !== undefined && { publicationDate: formState.publicationDate }),
    ...(formState.publicationTime !== undefined && { publicationTime: formState.publicationTime }),
    ...(formState.publicationTimezone !== undefined && { publicationTimezone: formState.publicationTimezone }),
    ...(formState.publicationOffsetMinutes !== undefined && { publicationOffsetMinutes: formState.publicationOffsetMinutes }),
  };

  const statusChanged = formState.initialDraftStatus !== undefined
    ? formState.draft_status !== formState.initialDraftStatus
    : formState.draft_status !== undefined;

  const hasValidSchedule =
    !!formState.publishedAt ||
    (!!formState.publicationDate && !!formState.publicationTime);
 
  const shouldIncludeStatus =
    !isFullValidation && (
      // 1) respect explicit status changes
      (formState.draft_status !== undefined && (statusChanged || formState.initialDraftStatus === 'queued'))
      // 2) or, implicitly queue if a schedule exists but status isn’t queued yet
      || (hasValidSchedule && formState.draft_status !== 'queued')
    );

  const statusField = shouldIncludeStatus
    ? { draft_status: formState.draft_status ?? 'queued' }
    : {};

  const artworkAbout = formState.artwork_about ?? {};
  const hasStartingPrice = hasValidAuctionStartingPrice(artworkAbout);
  const isAuctionDraft = Boolean(artworkAbout.artwork_is_auction) || hasStartingPrice;
  // Ensure auction drafts always include the starting price and remain explicitly flagged
  // so backend validation can pivot away from the standard `artwork_price` checks.
  const normalizedArtworkAbout = isAuctionDraft
    ? {
      ...artworkAbout,
      artwork_is_auction: true,
      price_starting_at: artworkAbout.price_starting_at ?? artworkAbout.artwork_price ?? '',
      artwork_price: (() => {
        const rawPrice = artworkAbout.artwork_price;
        if (typeof rawPrice === 'string' && rawPrice.trim() === '') {
          return artworkAbout.price_starting_at ?? '';
        }
        if (rawPrice === undefined || rawPrice === null) {
          return artworkAbout.price_starting_at ?? '';
        }
        return rawPrice;
      })(),
    }
    : { ...artworkAbout };

  if (!isAuctionDraft && Object.prototype.hasOwnProperty.call(normalizedArtworkAbout, 'price_starting_at')) {
    delete normalizedArtworkAbout.price_starting_at;
  }

  const normalizedArtworkAuction = (isAuctionDraft || formState.artwork_auction)
    ? {
      ...(formState.artwork_auction ?? {}),
      ends_at: formState.artwork_auction?.ends_at ?? null,
    }
    : null;

  const draftData = {
    artwork_about: normalizedArtworkAbout,
    artwork_media: formState.artwork_media,
    artwork_shipping: formState.artwork_shipping,
    ...(normalizedArtworkAuction ? { artwork_auction: normalizedArtworkAuction } : {}),
    lastCompletedStep: validationOptions ? formState.lastCompletedStep + 1 : formState.lastCompletedStep,
    validation: validationOptions,
    ...scheduleFields,
    ...statusField,
  };

  if (formState._id) {
    return fetchWrapper.put(`${config.apis.api.url}/draft/${formState._id}`, {
      injectToken: true,
      body: {
        draft_container: draftData,
        ...scheduleFields,
        ...statusField,
      }
    });
  } else {
    return fetchWrapper.post(`${config.apis.api.url}/draft`, {
      injectToken: true,
      body: {
        draft_container: draftData,
        ...scheduleFields,
        ...statusField,
      }
    });
  }
}

async function castPollVote(postId, pollId, optionIndex) {
  const { data } = await fetchWrapper.post(
    `${config.apis.api.url}/post/${postId}/polls/${pollId}/vote`,
    {
      injectToken: true,
      body: { optionIndex }
    }
  );
  return data.poll;
}

async function castQuizAnswer(postId, quizId, answerIndex) {
  const { response } = await fetchWrapper.post(
    `${config.apis.api.url}/post/${postId}/quizzes/${quizId}/answer`,
    { injectToken: true, body: { answerIndex } }
  );
  if (!response.ok) throw new Error('Quiz answer failed');
}

function editArtwork(formState) {
  return fetchWrapper.put(`${config.apis.api.url}/artwork/${formState._id}`, {
    injectToken: true,
    body: {
      draft_container: {
        ...formState,
        validation: { about: true, media: true, shipping: true }
      }
    }
  });
}

function updateGalleryShippings(newGalleryShippings) {
  return fetchWrapper.put(`${config.apis.api.url}/gallery/shipping`, {
    injectToken: true,
    body: {
      gallery_shippings: newGalleryShippings
    }
  });
}

function createArtistProfile(newArtistData, createMode) {
  return fetchWrapper.post(`${config.apis.api.url}/galleryartist`, {
    injectToken: true,
    body: {
      ...newArtistData,
      type: createMode
    }
  });
}

function editArtistProfile(newArtistData) {
  return fetchWrapper.put(`${config.apis.api.url}/galleryartist/${newArtistData._id}`, {
    injectToken: true,
    body: newArtistData
  });
}

function completeArtistProfile(newEmail, artistData) {
  return fetchWrapper.put(`${config.apis.api.url}/galleryartist/${artistData._id}/complete`, {
    injectToken: true,
    body: {
      ...artistData,
      artist_email: newEmail.toLowerCase()
    }
  });
}

function completeOnboarding() {
  return fetchWrapper.put(`${config.apis.api.url}/gallery/onboarding/complete`, {
    injectToken: true,
  });
}

function removePostDraft(draftId) {
  return fetchWrapper.delete(`${config.apis.api.url}/post/${draftId}`, {
    injectToken: true,
  });
}

function editGalleryAgent(newGalleryAgent) {
  return fetchWrapper.put(`${config.apis.api.url}/gallery/info`, {
    injectToken: true,
    body: {
      gallery_agent: newGalleryAgent
    }
  });
}

function updateBankAccount(newBankAccount) {
  return fetchWrapper.put(`${config.apis.api.url}/gallery/bank`, {
    injectToken: true,
    body: newBankAccount
  });
}

function getGalleryPricing(galleryId = null) {
  return fetchWrapper.get(`${config.apis.api.url}/gallery/pricing`, {
    injectToken: true,
    headers: galleryId ? { 'x-gallery-id': galleryId } : undefined,
  });
}

function updateGalleryPricing(defaultArtworkCurrency) {
  return fetchWrapper.put(`${config.apis.api.url}/gallery/pricing`, {
    injectToken: true,
    body: { default_artwork_currency: defaultArtworkCurrency },
  });
}

function createStripeConnectOnboardingLink() {
  return fetchWrapper.post(`${config.apis.api.url}/gallery/connect/onboarding-link/`, {
    injectToken: true,
  });
}

function createStripeConnectLoginLink() {
  return fetchWrapper.post(`${config.apis.api.url}/gallery/connect/login-link/`, {
    injectToken: true,
  });
}

function refreshStripeConnectStatus() {
  return fetchWrapper.post(`${config.apis.api.url}/gallery/connect/status/`, {
    injectToken: true,
  });
}

function getStripeConnectCountries() {
  return fetchWrapper.get(`${config.apis.api.url}/gallery/connect/countries/`, {
    injectToken: true,
  });
}

function updateClosedMode(closed, reason) {
  return fetchWrapper.put(`${config.apis.api.url}/gallery/closed`, {
    injectToken: true,
    body: {
      closed,
      reason
    }
  });
}

function getAllPosts(params) {
  return fetchWrapper.get(`${config.apis.api.url}/posts`, { params, injectToken: true });
}

function getGalleryOfferSettings(galleryId = null) {
  return fetchWrapper.get(`${config.apis.api.url}/gallery/offers/settings`, {
    injectToken: true,
    headers: galleryId ? { 'x-gallery-id': galleryId } : undefined,
  });
}

function getIncompletePosts() {
  return fetchWrapper.get(`${config.apis.api.url}/posts/incomplete`, { injectToken: true });
}

/**
 * Generic list‐fetcher for posts or articles by user type.
 * @param {"gallery"|"artist"} userType
 * @param {"posts"|"articles"} listType
 * @param {string} userId
 */
function getListByUser(userType, listType, userId) {
  return fetchWrapper.get(
    `${config.apis.api.url}/posts/${userType}/${listType}/${userId}`,
    { injectToken: true }
  );
}

function getPostsByGalleryId(galleryId) {
  return getListByUser("gallery", "posts", galleryId);
}
function getPostsByArtistId(artistId) {
  return getListByUser("artist", "posts", artistId);
}

function getPostById(postId) {
  return fetchWrapper.get(`${config.apis.api.url}/post/${postId}`, {
    injectToken: true
  });
}

function getArticleById(articleId) {
  return fetchWrapper.get(`${config.apis.api.url}/post/${articleId}`, {
    injectToken: true
  });
}

async function getPublicContentWithSeoById(contentId) {
  const [{ response: contentResponse, data: contentData }, { response: seoResponse, data: seoData }] = await Promise.all([
    fetchWrapper.get(`${config.apis.api.url}/post/${contentId}`, {
      injectToken: true,
    }),
    fetchWrapper.get(`${config.apis.api.url}/seo/content/${contentId}`, {
      injectToken: true,
    }),
  ]);

  if (!contentResponse?.ok || !contentData) {
    const error = new Error('Failed to load content');
    error.response = contentResponse;
    throw error;
  }

  return {
    response: contentResponse,
    data: seoResponse?.ok && seoData
      ? {
        ...contentData,
        __seo: seoData,
      }
      : contentData,
  };
}

function submitPost(postData, validation = false) {
  // Split schedule fields (must be top-level) from the rest (post_container)
  const {
    // schedule fields expected by backend at TOP LEVEL:
    publishedAt,
    publicationDate,
    publicationTime,
    publicationTimezone,
    publicationOffsetMinutes,
    // optional UI status (backend will compute anyway)
    post_status,
    // keep id for PUT, remove for POST
    _id,
    // everything else is article/post content → post_container
    ...post_container
  } = postData || {};

  const scheduleFields = {
    ...(publishedAt !== undefined && { publishedAt }),
    ...(publicationDate !== undefined && { publicationDate }),
    ...(publicationTime !== undefined && { publicationTime }),
    ...(publicationTimezone !== undefined && { publicationTimezone }),
    ...(publicationOffsetMinutes !== undefined && { publicationOffsetMinutes }),
  };

  const statusField =
    post_status !== undefined
      ? { post_status }
      : {};

  // Build request body: top-level schedule fields + post_container
  const body = {
    validation: Boolean(validation),
    post_container: {
      ...post_container,
      ...scheduleFields,
      ...statusField,
    },
    ...scheduleFields,
    ...statusField,
  };

  if (_id) {
    // UPDATE existing post
    return fetchWrapper.put(
      `${config.apis.api.url}/post/${_id}`,
      { injectToken: true, body }
    );
  } else {
    // CREATE new post
    return fetchWrapper.post(
      `${config.apis.api.url}/post`,
      { injectToken: true, body }
    );
  }
}

function createPostVideoDirectUpload(file) {
  return fetchWrapper.post(`${config.apis.api.url}/post/videos/direct-upload`, {
    injectToken: true,
    body: file,
  });
}

function getPostVideoUploadStatus(assetId) {
  return fetchWrapper.get(`${config.apis.api.url}/post/videos/${encodeURIComponent(assetId)}/status`, {
    injectToken: true,
  });
}

function getPostVideoPlayback(postId, assetId) {
  return fetchWrapper.get(
    `${config.apis.api.url}/post/${encodeURIComponent(postId)}/videos/${encodeURIComponent(assetId)}/playback`,
    { optionalToken: true }
  );
}

function deletePostVideoUpload(assetId) {
  return fetchWrapper.delete(`${config.apis.api.url}/post/videos/${encodeURIComponent(assetId)}`, {
    injectToken: true,
  });
}

/**
 * Like a post
 * @param {string} postId
 */
async function likePost(postId) {
  const { data } = await fetchWrapper.post(
    `${config.apis.api.url}/post/like/${postId}`,
    {
      injectToken: true,
      body: {}
    }
  );
  return data;
}

/**
 * Unlike a post
 * @param {string} postId
 */
async function unlikePost(postId) {
  const { data } = await fetchWrapper.post(
    `${config.apis.api.url}/post/unlike/${postId}`,
    {
      injectToken: true,
      body: {}
    }
  );
  return data;
}

// Create a new comment on a resource
async function createComment(resourceType, resourceId, text, parentId = null) {
  console.log('[GalleryService] posting to:', `${config.apis.api.url}/${resourceType}/${resourceId}/comments`);
  return fetchWrapper.post(
    `${config.apis.api.url}/${resourceType}/${resourceId}/comments`,
    {
      injectToken: true,
      body: { text, parentId }
    }
  );
}

// Fetch comments for a resource
async function getComments(resourceType, resourceId, { limit = 10, offset = 0 } = {}) {
  // does fetchWrapper.get actually return { response, data }?
  const result = await fetchWrapper.get(
    `${config.apis.api.url}/${resourceType}/${resourceId}/comments`,
    { params: { limit, offset }, injectToken: true }
  );
  // result should be { response: FetchResponse, data: [...] }
  return result.data;
}

// Fetch replies for a single comment on any resource (post, article, etc.)
async function getCommentReplies(resourceType, resourceId, commentId) {
  const { data } = await fetchWrapper.get(
    `${config.apis.api.url}/${resourceType}/${resourceId}/comments/${commentId}/replies`,
    { injectToken: true }
  );
  return data; 
}

/**
 * Like a comment (matches your route definition)
 * @param {string} commentId
 */
async function likeComment(commentId) {
  // must pass a (possibly empty) body to fetchWrapper.post
  const { data } = await fetchWrapper.post(
    `${config.apis.api.url}/comment/like/${commentId}`,
    {},                    // empty JSON body
    { injectToken: true }  // send auth header
  );
  return data;
}

/**
 * Unlike a comment
 * @param {string} commentId
 */
async function unlikeComment(commentId) {
  const { data } = await fetchWrapper.post(
    `${config.apis.api.url}/comment/unlike/${commentId}`,
    {},                    // empty JSON body
    { injectToken: true }
  );
  return data;
}

function markArtworkAsSold(artworkId, collectorEmail) {
  return fetchWrapper.post(`${config.apis.api.url}/mark-as-sold/${artworkId}`, {
    injectToken: true,
    body: {
      user_email: collectorEmail.toLowerCase()
    }
  });
}

function sellInStore(artworkId, payload) {
  return fetchWrapper.post(`${config.apis.api.url}/gallery/artwork/${artworkId}/sell-in-store`, {
    injectToken: true,
    body: payload,
  });
}

function listGalleryContacts() {
  return fetchWrapper.get(`${config.apis.api.url}/gallery/contacts`, {
    injectToken: true,
  });
}

function requestLimitedEditions(artworkId) {
  return fetchWrapper.post(`${config.apis.api.url}/inventory/limited-editions/${artworkId}/request`, {
    injectToken: true,
  });
}

function changeRenewalOfContract(artworkId, isAutomatic) {
  fetchWrapper.post(`${config.apis.api.url}/inventory/automatic-renewal/${artworkId}`, {
    injectToken: true,
    body: {
      is_automatic: isAutomatic
    }
  });
}

function withdrawArtwork(artworkId) {
  return fetchWrapper.post(`${config.apis.api.url}/inventory/withdraw/${artworkId}`, {
    injectToken: true,
  });
}

function submitShippingDetails(orderNumber, shippingDetails) {
  return fetchWrapper.put(`${config.apis.api.url}/order/${orderNumber}/tracking`, {
    injectToken: true,
    body: shippingDetails
  });
}

function getStickers(galleryId) {
  return fetchWrapper.get(`${config.apis.api.url}/gallery/${galleryId}/stickers`, {
    injectToken: true,
  });
}

function createPermissions(artistId, permissions, galleryId) {
  return fetchWrapper.post(`${config.apis.api.url}/permissions`, {
    injectToken: true,
    body: {
      artist: artistId,
      permissions,
      ...(galleryId ? { gallery_id: galleryId } : {})
    }
  });
}

function updatePermissions(permissionsId, newPermissions, galleryId) {
  return fetchWrapper.put(`${config.apis.api.url}/permissions/${permissionsId}`, {
    injectToken: true,
    body: {
      permissions: newPermissions,
      ...(galleryId ? { gallery_id: galleryId } : {})
    }
  });
}

function sendInviteToArtist(artistId, artistEmail = null) {
  const body = artistEmail ? { artist_email: artistEmail } : undefined;
  return fetchWrapper.put(`${config.apis.api.url}/invite/${artistId}`, {
    injectToken: true,
    ...(body ? { body } : {})
  });
}

const GalleryService = {
  removeArtworkDraft,
  deleteArtwork,
  getArtworkForEdit,
  saveArtworkDraft,
  castPollVote,
  castQuizAnswer,
  editArtwork,
  updateGalleryShippings,
  createArtistProfile,
  completeArtistProfile,
  editArtistProfile,
  completeOnboarding,
  removePostDraft,
  editGalleryAgent,
  updateBankAccount,
  getGalleryPricing,
  updateGalleryPricing,
  createStripeConnectOnboardingLink,
  createStripeConnectLoginLink,
  refreshStripeConnectStatus,
  getStripeConnectCountries,
  updateClosedMode,
  getAllPosts,
  getGalleryOfferSettings,
  getPostById,
  getIncompletePosts,
  getPostsByGalleryId,
  getPostsByArtistId,
  getArticleById,
  getPublicContentWithSeoById,
  submitPost,
  createPostVideoDirectUpload,
  getPostVideoUploadStatus,
  getPostVideoPlayback,
  deletePostVideoUpload,
  likePost,
  unlikePost,
  createComment,
  getComments,
  getCommentReplies,
  likeComment,
  unlikeComment,
  markArtworkAsSold,
  listGalleryContacts,
  requestLimitedEditions,
  changeRenewalOfContract,
  withdrawArtwork,
  submitShippingDetails,
  getStickers,
  createPermissions,
  sellInStore,
  updatePermissions,
  sendInviteToArtist
};

export default GalleryService;
